import { useEffect, useState } from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link,
} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { Compass, Loader2 } from 'lucide-react';
import { LanguageSelector } from '@/components/LanguageSelector';
import {
  isKnownTownSlug,
  resolvePersona,
  type Persona,
} from '@/lib/personas';
import {
  clearAllAppStorage,
  clearPendingRef,
  writePendingRef,
  writePersonalPage,
} from '@/lib/storage';
import {
  completeOnboarding,
  redeemInvitation,
  type PublicPartner,
} from '@/lib/api/users';
import { ApiError } from '@/lib/api';
type JoinState = 'validating' | 'joined' | 'invalid';
/**
 * Loose pre-check on the URL slug before round-tripping to the API.
 * Town slugs are lowercase letters with optional internal hyphens
 * (e.g. `padstow`, `st-ives`) — matches the `towns.ts` allow-list
 * shape in the API repo. Saves a network call on obviously malformed
 * URLs. The API is the authoritative validator — this is only an
 * early-exit, not a security check.
 *
 * Pattern accepts: `padstow`, `st-ives`, `a-b-c`.
 * Pattern rejects: empty, leading/trailing hyphen, double hyphen,
 *   uppercase, digits, anything else outside `[a-z-]`.
 */
const TOWN_SLUG_PATTERN = /^[a-z]+(-[a-z]+)*$/;
/**
 * Length bound for the captured `?ref=` partner slug. Matches the API's
 * `CompleteSchema.ref` AND `RedeemSchema.ref` bounds exactly
 * (`z.string().min(1).max(64)`).
 *
 * Deliberately no slug-format regex: an unknown OR malformed-but-bounded
 * ref resolves to `partner: null` server-side (the API looks it up
 * against `partners.slug` and falls through on miss). A stale or
 * mistyped QR must never break onboarding — attribution is best-effort,
 * sign-up is not. Anything outside the length bound is dropped silently
 * here.
 */
const REF_MIN_LENGTH = 1;
const REF_MAX_LENGTH = 64;
export default function JoinPage(): JSX.Element | null {
  const { townSlug } = useParams<{ townSlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Clerk auth state — drives the lazy-first-touch attribution branch
  // on the "Open my page" button below. `isSignedIn` flips true when
  // an existing Clerk session is detected (cross-subdomain SSO from
  // the partner dashboard, a returning guest, etc.).
  const { isSignedIn, getToken } = useAuth();
  // Clerk user details — surfaces the signed-in identity to the user
  // via the joined-state notice. Without this, a returning guest with
  // a cross-subdomain session (e.g. partner-dashboard SSO) would be
  // silently routed into their existing account when they tap "Open
  // my page", and might never realise it. Premium UX surfaces the
  // identity explicitly so the user makes a conscious choice rather
  // than the system falling through. See handleSignOut below.
  const { user } = useUser();
  // Clerk client — exposes signOut() for the "use a different account"
  // affordance on the signed-in notice. Distinct hook from useAuth /
  // useUser; useClerk gives the full Clerk client object.
  const { signOut } = useClerk();
  // TanStack Query client — used to invalidate the ['users', 'me']
  // cache after a successful lazy-attribution call so MePage fetches
  // the freshly-attributed user record on its first render. Without
  // this, MePage would render once with stale (pre-attribution) /me
  // data and show the wrong persona for a tick before the cache
  // naturally refreshes.
  const queryClient = useQueryClient();
  // i18n wiring (Phase 4a-2b, 8 Jun 2026). The `joinPage` namespace
  // is registered eagerly in src/i18n/index.ts so `t()` works
  // synchronously from first render. Type-safe key inference via the
  // module augmentation in src/types/i18next.d.ts — wrong keys fail
  // tsc, not at runtime.
  const { t } = useTranslation('joinPage');
  const [state, setState] = useState<JoinState>('validating');
  const [persona, setPersona] = useState<Persona | null>(null);
  const [partner, setPartner] = useState<PublicPartner | null>(null);
  // Guards the "Open my page" button while the lazy-attribution call
  // (if any) is in flight. Prevents double-fires from rapid taps,
  // shows visual loading state via opacity.
  const [isOpening, setIsOpening] = useState(false);
  // Guards the "use a different account" affordance while the Clerk
  // signOut call is in flight. Prevents double-fires AND blocks the
  // primary "Open my page" CTA during the transition (you don't want
  // the user able to proceed as the existing account at the exact
  // moment they've asked to sign out of it).
  const [isSigningOut, setIsSigningOut] = useState(false);
  /*
   * Language selector layer (Phase 4b-3, 8 Jun 2026). Rendered once
   * per state branch in a fixed-position top-right pill. Defined here
   * as a JSX const so the markup lives in one place rather than
   * duplicated across the three early-return branches.
   *
   * Fixed positioning lets it hover above whatever main content is
   * showing without disrupting the centering layouts that each state
   * uses (validating + invalid are vertically-centred; joined uses
   * flex-col justify-center). `safe-top` keeps the pill clear of the
   * iOS notch. `pointer-events-none` on the outer container with
   * `pointer-events-auto` on the pill means taps in the empty top
   * strip fall through to the underlying content — only the pill
   * itself is interactive.
   *
   * Rendered in all three states (validating / invalid / joined) so a
   * French-browser guest landing on a broken QR can read the error in
   * French and a successful-join guest can switch language before chat
   * starts. Hiding it during the brief validating state would also
   * cause a layout shift when it appeared later — premium discipline
   * says render it once and let it stay.
   */
  const selectorLayer = (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-10 flex justify-end p-4 safe-top">
      <div className="pointer-events-auto">
        <LanguageSelector />
      </div>
    </div>
  );
  /**
   * Capture OR CLEAR the `?ref=...` attribution, eagerly and
   * independently of the redeem flow. The current URL is authoritative
   * for what attribution this visit represents:
   *   - URL has a valid ref → write/overwrite pendingRef
   *   - URL has no ref      → clear any stale pendingRef
   *   - URL has invalid ref → clear any stale pendingRef
   *
   * Clear-on-no-ref invariant matters because pendingRef persists
   * across page loads until either consumed (on successful sign-up by
   * OnboardingCompletePage) or wiped (on sign-out by
   * clearAllAppStorage). Without the clear, a guest who visited
   * /join/padstow?ref=foo earlier — e.g. tested a partner QR, then
   * bailed before signup — and later returned to /join/padstow with
   * no ref would still get attributed to foo on signup. That's the
   * symptom that surfaced during Padstow testing on 5 Jun 2026
   * (Morwenna expected on a clean signup, Ollie shown because a
   * stale `pendingRef = harlyn-sands` from an earlier session was
   * still in localStorage and got consumed at /invitation/complete).
   *
   * Eager: writes (and clears) fire before the redeem network call
   * returns, so even if the user leaves the page mid-validation —
   * closing the tab, switching away, network blip — pendingRef
   * already reflects this visit's URL.
   *
   * Independent: a separate effect from the redeem call so that ref
   * capture is not coupled to redeem success/failure paths, and so
   * the redeem effect's dependency array doesn't take `searchParams`
   * and accidentally re-fire on a URL search-string change.
   */
  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (
      refParam !== null &&
      refParam.length >= REF_MIN_LENGTH &&
      refParam.length <= REF_MAX_LENGTH
    ) {
      writePendingRef({ ref: refParam });
    } else {
      clearPendingRef();
    }
  }, [searchParams]);
  useEffect(() => {
    if (!townSlug || !TOWN_SLUG_PATTERN.test(townSlug)) {
      setState('invalid');
      return;
    }
    /*
     * Read the ref directly from `searchParams` here (in addition to
     * the eager-capture effect above) so we can pass it to the redeem
     * call and receive the PublicPartner projection in the response.
     *
     * Read fresh from `searchParams` rather than from storage — keeps
     * the data path one-way (URL → redeem call) and avoids coupling
     * this effect's success path to the write-order of the storage
     * effect.
     *
     * `searchParams` is intentionally NOT in this effect's dep array
     * (see eslint-disable below): redeem must not re-run on a URL
     * ref-only change. Ref capture is handled separately above.
     */
    const refParam = searchParams.get('ref');
    const refToSend =
      refParam !== null &&
      refParam.length >= REF_MIN_LENGTH &&
      refParam.length <= REF_MAX_LENGTH
        ? refParam
        : undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const result = await redeemInvitation({
          townSlug,
          ...(refToSend !== undefined ? { ref: refToSend } : {}),
          signal: controller.signal,
        });
        // The API may know towns we don't have personas for yet (during
        // a new-town rollout, the server seeds the town first; the app
        // catches up on the next deploy). Treat as invalid for the user.
        if (!isKnownTownSlug(result.town.slug)) {
          setState('invalid');
          return;
        }
        // Resolve the EFFECTIVE persona for the joining page:
        //   - Partner persona (e.g. Ollie at Harlyn Sands) when
        //     attribution applies AND partner.persona is populated
        //     (Batch 7b-1 API projection).
        //   - Town persona (Morwenna) otherwise — bare URL, unknown
        //     ref, town-mismatched ref, or partner with no curated
        //     persona content.
        // Returns null only when the town slug is not recognised; the
        // isKnownTownSlug guard above already filters that case, but
        // resolvePersona's own null-return is the defensive fallback.
        const resolvedPersona = resolvePersona(
          result.town.slug,
          result.partner,
        );
        if (!resolvedPersona) {
          setState('invalid');
          return;
        }
        // Write personalPage so MePage can pick up where this left off.
        // `token` field is retained for schema backwards-compat with any
        // existing entries in this browser's localStorage; it now holds
        // the town slug (same as `town`). The schema may shrink to just
        // {townSlug, joinedAt} in a future batch once MePage and other
        // readers are confirmed not to depend on the `token` field.
        writePersonalPage({
          token: result.town.slug,
          town: result.town.slug,
          joinedAt: new Date().toISOString(),
        });
        setPersona(resolvedPersona);
        // result.partner is null whenever no attribution applies
        // (bare URL, unrecognised ref, archived partner, town-mismatch).
        // The render path below falls back cleanly to the unbranded
        // hero in that case — Padstow's no-partner experience is
        // byte-identical to pre-5b.
        setPartner(result.partner);
        setState('joined');
      } catch (err) {
        if (controller.signal.aborted) return;
        // ApiError covers 404 (town not recognised) and any other 4xx/5xx
        // / network failures. For Batch 23 we treat them all as "link not
        // valid". Specific UX per error code lands later if needed.
        if (err instanceof ApiError) {
          setState('invalid');
          return;
        }
        console.error('[JoinPage] redeemInvitation failed', err);
        setState('invalid');
      }
    })();
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [townSlug]);
  /**
   * "Open my page" button handler. Handles two cases:
   *
   *   - Unauthenticated user (the canonical fresh-guest flow): plain
   *     navigate('/me'). Route guard bounces to Clerk sign-in/sign-up,
   *     and OnboardingCompletePage's reconcile path consumes pendingRef
   *     after signup to attribute the new user to the partner.
   *
   *   - Authenticated user (the lazy-first-touch flow, 10 Jun 2026):
   *     POST /v1/users/invitation/complete BEFORE navigating, with the
   *     ref from the URL. OnboardingCompletePage does NOT run for
   *     already-authenticated users (they go straight to /me), so
   *     without this call the pendingRef in localStorage would sit
   *     unconsumed and the user would see the town persona despite
   *     the QR pointing them at a partner experience. The API call is
   *     idempotent — first-touch immutability is preserved server-
   *     side, so an existing user with a non-null partner_ref is a
   *     no-op; an existing user with NULL partner_ref gets attributed.
   *
   * Why this case exists at all:
   *   - Partner-dashboard admins sharing a Clerk instance with the
   *     guest app (Mick's own debugging scenario)
   *   - Returning guests who originally signed up bare (no QR) and
   *     are now scanning a partner QR on a return visit
   *   - Support staff testing the guest flow without a fresh device
   *
   * Failure handling: any error from completeOnboarding is swallowed
   * — we still navigate to /me. The user's existing account state
   * (whatever it is) renders on MePage. Better to ship them into the
   * app on a flaky-network blip than to block the only action on the
   * page.
   *
   * `isOpening` guard prevents double-fires from rapid taps. The
   * button is also `disabled={isOpening}` for visual feedback; a
   * second tap during the in-flight call returns immediately at the
   * top of this function.
   */
  const handleOpenMyPage = async () => {
    if (isOpening) return;
    setIsOpening(true);
    if (isSignedIn && townSlug) {
      const refParam = searchParams.get('ref');
      const refToSend =
        refParam !== null &&
        refParam.length >= REF_MIN_LENGTH &&
        refParam.length <= REF_MAX_LENGTH
          ? refParam
          : undefined;
      try {
        await completeOnboarding({
          townSlug,
          getToken,
          ...(refToSend !== undefined ? { ref: refToSend } : {}),
        });
        // Invalidate the /me cache so MePage's useUser/usePartner
        // hooks fetch the freshly-attributed user record on first
        // render. Awaited so the navigate below fires only after
        // the invalidation completes — otherwise React Router
        // mounts MePage before the cache marker has flipped, and
        // MePage's first useUser read serves the pre-attribution
        // snapshot.
        await queryClient.invalidateQueries({
          queryKey: ['users', 'me'],
        });
      } catch {
        // Non-fatal — proceed to /me regardless. MePage handles
        // whatever state the user lands in.
      }
    }
    navigate('/me');
  };
  /**
   * "Use a different account" handler — added 10 Jun 2026 to close
   * the silent-session-reuse gap surfaced during Mick's Harlyn-eve
   * testing. The cross-subdomain Clerk session shared from the
   * partner dashboard meant any "fresh signup" attempt was silently
   * routed into the existing admin account, with no UI cue to the
   * user that they were not actually signing up as a new identity.
   *
   * Clears ALL client state before ending the Clerk session, then
   * signs out back to this same join page.
   *
   * This is the CANONICAL sign-out cleanup, identical to
   * SettingsPage.handleSignOut: `clearAllAppStorage()` wipes the
   * `ti:*` localStorage namespace, `queryClient.clear()` wipes the
   * fetched-data cache, and only then does `signOut()` end the Clerk
   * session. The order matters — clearing before signOut means any
   * component reading state mid-transition sees a clean slate.
   *
   * Why this is non-negotiable here: both stores are keyed to the
   * BROWSER, not the account. `ti:chatHistory` is a single global
   * key (not namespaced per user), and the `['users','me']` query
   * key is identical for every account. If a sign-out skips these
   * clears, the NEXT account signed in on this device inherits the
   * previous account's chat history (MePage hydrates it via
   * readChatHistory) AND the previous account's /me envelope (header
   * persona, theme, partner). The visible symptom is a crossover:
   * the header renders one account's persona while the chat body
   * shows another account's messages. The original version of this
   * handler called signOut() alone and produced exactly that.
   *
   * pendingRef re-seed: clearAllAppStorage wipes `ti:pendingRef`
   * along with everything else, but we want the partner attribution
   * context to survive into the next sign-up (the whole point of
   * "use a different account" on a partner join page is to sign up
   * fresh AS a guest of THIS partner). We re-seed it explicitly from
   * the current URL's `?ref=` rather than relying on the eager
   * capture effect re-firing — signOut redirects back to this same
   * URL, and React Router does not remount on an identical path, so
   * that effect's `searchParams` dependency never changes. The
   * validation bounds mirror that effect exactly.
   *
   * Redirect target: we pass an explicit redirectUrl back to this
   * same /join/:townSlug?ref=... so the destination is deterministic
   * (not Clerk's default afterSignOutUrl), keeping the guest on the
   * partner-branded join page rather than bouncing them to the
   * generic root.
   *
   * Failure handling: signOut() failure is rare but possible
   * (network blip, Clerk SDK error). We log for ops visibility and
   * reset the spinner so the user can retry. The storage/cache
   * clears have already run by then — harmless, since they only
   * remove data the user was about to discard anyway.
   *
   * `isSigningOut` blocks both this affordance AND the primary
   * "Open my page" CTA during the transition. Letting the user
   * proceed as the existing account during the sign-out transition
   * would defeat the purpose of the choice they just made.
   */
  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);

    // Wipe browser-keyed client state so the next account on this
    // device starts clean. See the handler comment for why this is
    // mandatory.
    clearAllAppStorage();
    queryClient.clear();

    // Re-seed pendingRef from the current URL so partner attribution
    // survives into the next sign-up. Mirrors the eager-capture
    // effect's validation bounds.
    const refParam = searchParams.get('ref');
    if (
      refParam !== null &&
      refParam.length >= REF_MIN_LENGTH &&
      refParam.length <= REF_MAX_LENGTH
    ) {
      writePendingRef({ ref: refParam });
    }

    // Return to this same join page after sign-out, preserving the
    // ?ref= partner context.
    const search = searchParams.toString();
    const returnUrl = `/join/${townSlug ?? ''}${
      search ? `?${search}` : ''
    }`;
    try {
      await signOut({ redirectUrl: returnUrl });
    } catch (err) {
      console.error('[JoinPage] signOut failed', err);
      setIsSigningOut(false);
    }
  };
  if (state === 'validating') {
    return (
      <>
        <Helmet>
          <title>{t('validating.title')}</title>
        </Helmet>
        {selectorLayer}
        <main
          id="main"
          className="container-narrow safe-top safe-bottom flex min-h-dvh items-center justify-center"
        >
          <div className="flex flex-col items-center gap-3 text-slate-600">
            <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin" />
            <p className="text-sm">{t('validating.settingUp')}</p>
          </div>
        </main>
      </>
    );
  }
  if (state === 'invalid') {
    return (
      <>
        <Helmet>
          <title>{t('invalid.title')}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        {selectorLayer}
        <main
          id="main"
          className="container-narrow safe-top safe-bottom flex min-h-dvh flex-col items-center justify-center text-center"
        >
          <h1 className="font-display text-3xl text-moor-800">{t('invalid.heading')}</h1>
          <p className="mt-3 text-slate-700">
            {t('invalid.description')}
          </p>
          <Link to="/" className="btn-primary mt-6">
            {t('invalid.backHome')}
          </Link>
        </main>
      </>
    );
  }
  // state === 'joined'. The validation effect only sets this when persona
  // is non-null, but TS can't see across the async boundary, so guard.
  if (!persona) return null;
  /*
   * Partner-aware visual treatment (Batch 5b, 7b-2b, 11).
   *
   * When `partner.theme.primaryHex` is present (a partner is attributed
   * AND has supplied a brand colour), the page takes on the partner's
   * colour through four touchpoints:
   *   - Page background → white (overrides the global cream surface
   *     via the wrapper div below — wrapping rather than refactoring
   *     <main>'s class list keeps `container-narrow`'s horizontal
   *     constraints intact)
   *   - Compass icon background → primaryHex
   *   - Heading "You're in." → primaryHex (text colour)
   *   - Primary button background → primaryHex; hover uses opacity-90
   *     (can't compute a darker Tailwind shade from an arbitrary hex
   *     without a colour library, opacity is the clean substitute and
   *     works with any partner primary)
   *
   * Welcome copy ALSO branches on partner attribution (independently
   * of theme): an attributed page leads with the partner name directly
   * ("Welcome to Harlyn Sands Holiday Park.") rather than the
   * "Padstow Insider" product framing — keeps the partner-led posture
   * consistent with Batch 8a's keepsake.
   *
   * The signed-in notice (added 10 Jun 2026) is deliberately NOT
   * partner-themed — it's informational chrome about TownInsider
   * identity, not partner-voiced content. Moor palette throughout
   * regardless of partner branding, same scoping principle as the
   * language selector and the chat input.
   *
   * No-partner path (Morwenna or any other town persona served bare)
   * is byte-identical to pre-Batch 11: global cream background, moor-
   * green heading, moor-green button, "Welcome to {Town} Insider."
   * copy. The mantra here is "never touch a town surface in a partner
   * batch" — partner branding earns the visual change, town personas
   * retain TownInsider's house style.
   *
   * `partner?.theme?.primaryHex` is optional all the way down:
   *   - partner may be null (no ref, unknown ref, archived, town-mismatch)
   *   - theme may be null (server-side jsonb parse/validation failure)
   *   - primaryHex may be undefined (partner hasn't supplied one yet)
   * Any of these fall back to the moor-green / cream house style.
   *
   * No CSS sanitisation here: the value is sourced from an
   * admin-curated jsonb column that the API server-side-validates
   * with `partnerThemeSchema`, whose `primaryHex` field is regex-
   * constrained to `^#[0-9A-Fa-f]{6}$` — a non-hex value can't
   * persist. Render via inline `style` (not into a `<style>` block
   * or innerHTML), which scopes the value to the element's `style`
   * attribute and prevents any CSS-injection escape even if the
   * regex were ever loosened.
   */
  const partnerPrimary = partner?.theme?.primaryHex;
  const partnerName = partner?.name;
  // Identity surfacing: when a Clerk session is active AND the user
  // object has loaded (it can briefly lag isSignedIn on cold-cache
  // mounts), capture the primary email for the signed-in notice
  // below. `user.primaryEmailAddress` is the Clerk-canonical primary —
  // a user can have multiple verified emails but only one is primary
  // at a time. Falls back to the first email address if the primary
  // pointer hasn't resolved yet (defensive — Clerk's SDK typically
  // sets both, but the JSX guard below also checks for empty).
  const signedInEmail =
    isSignedIn && user
      ? user.primaryEmailAddress?.emailAddress ??
        user.emailAddresses[0]?.emailAddress ??
        null
      : null;
  return (
    <>
      <Helmet>
        <title>{t('joined.title', { townLabel: persona.townLabel })}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {selectorLayer}
      {/*
        Outer wrapper hosts the partner-attributed white background.
        Wrapping rather than refactoring <main>'s class list keeps
        `container-narrow`'s horizontal constraints intact and avoids
        refactor risk. The wrapper takes natural height from main's
        `min-h-dvh`, so `bg-white` reliably covers the viewport behind
        and around the constrained main column. No-partner case: the
        wrapper renders without a class and the global body cream
        shows through — byte-identical to pre-Batch 11.
      */}
      <div className={partnerPrimary ? 'bg-white' : undefined}>
        <main
          id="main"
          className="container-narrow safe-top safe-bottom flex min-h-dvh flex-col justify-center"
        >
          <div className="animate-slide-up">
            <div
              className={`mb-6 inline-flex h-14 w-14 items-center justify-center rounded-full text-white ${
                partnerPrimary ? '' : 'bg-moor-700'
              }`}
              style={
                partnerPrimary
                  ? { backgroundColor: partnerPrimary }
                  : undefined
              }
            >
              <Compass aria-hidden="true" className="h-7 w-7" />
            </div>
            <h1
              className={`font-display text-4xl sm:text-5xl ${
                partnerPrimary ? '' : 'text-moor-800'
              }`}
              style={
                partnerPrimary ? { color: partnerPrimary } : undefined
              }
            >
              {t('joined.heading')}
            </h1>
            <p className="mt-3 text-lg text-slate-700">
              {partnerName
                ? t('joined.welcomeToPartner', { partnerName })
                : t('joined.welcomeToTown', { townLabel: persona.townLabel })}{' '}
              {t('joined.personaReady', { personaName: persona.personaName })}
            </p>
            {/*
              Marketing blurb (Batch 8a-2): "Photos" removed from the
              promised capabilities. The photo + voice-note UI surface is
              hidden in ChatInput until the upload + transcription
              pipelines land — we don't market what we don't ship. When
              those features come back, restore the wording here in the
              same batch.
            */}
            <p className="mt-6 text-sm text-slate-600">
              {t('joined.bookmarkHint')}
            </p>
            {/*
              Signed-in notice (10 Jun 2026): when an existing Clerk
              session is detected, surface the active identity so the
              user makes an explicit choice — continue as this user
              via the primary CTA below, or sign out and start fresh
              via the inline affordance here. Closes the silent-
              session-reuse gap: previously, a returning guest or a
              cross-subdomain admin landing here would be invisibly
              routed into their existing account when they tapped
              "Open my page", with no UI cue that the system had
              substituted an identity for them.

              Render guard:
                - isSignedIn must be true (no notice for fresh guests)
                - signedInEmail must be a non-null string (Clerk SDK
                  user object can briefly lag isSignedIn during cold
                  cache mounts; render the notice only when we have
                  a concrete email to display)

              Styling:
                - Moor palette regardless of partner branding —
                  identity chrome, not partner-voiced content
                - Subtle bg, no harsh "warning" connotation; this is
                  informational, not an error
                - Inline button rather than a separate primary action
                  — the choice is contextual to the "you're in"
                  moment, not a competing CTA
            */}
            {isSignedIn && signedInEmail && (
              <div className="mt-6 rounded-xl border border-moor-200 bg-moor-50 p-4">
                <p className="text-sm text-moor-900">
                  {t('joined.signedInAs', { email: signedInEmail })}
                </p>
                <button
                  type="button"
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  className="mt-2 text-sm text-moor-700 underline underline-offset-4 transition-colors hover:text-moor-900 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
                >
                  {isSigningOut
                    ? t('joined.signingOut')
                    : t('joined.useDifferentAccount')}
                </button>
              </div>
            )}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {/*
                Button (Batch 11 + lazy-first-touch 10 Jun 2026 + sign-
                out guard 10 Jun 2026): when `partnerPrimary` is
                present we override `btn-primary`'s background via
                inline style and add `hover:opacity-90` for the hover
                affordance. Inline style outranks the class-based
                `hover:bg-moor-800` via CSS specificity, so the bg
                stays in partner colour on hover; `opacity-90`
                provides the darken cue without needing a colour
                library to compute a shade off the partner hex. No-
                partner case: `btn-primary` alone, no override —
                identical to pre-Batch 11.
                The onClick is async (handleOpenMyPage) and disabled
                while EITHER the lazy-first-touch attribution call
                OR the sign-out call is in flight. The dual-gate
                matters: letting the user proceed as the existing
                account during a sign-out transition would defeat
                the purpose of the choice they just made on the
                signed-in notice above.
              */}
              <button
                type="button"
                onClick={handleOpenMyPage}
                disabled={isOpening || isSigningOut}
                className={`btn-primary ${
                  partnerPrimary ? 'hover:opacity-90' : ''
                } ${isOpening || isSigningOut ? 'cursor-not-allowed opacity-70' : ''}`}
                style={
                  partnerPrimary
                    ? { backgroundColor: partnerPrimary }
                    : undefined
                }
              >
                {t('joined.openMyPage')}
              </button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
